import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { roleTaxonomySelectionToAiSearchId } from '../../../../shared/selection-lists/utils/roleTaxonomy';
import { UserPreferenceKey } from '../../../../shared/user-preferences/types';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import { getPreferenceScope } from '../../../../shared/user-preferences/utils/preferences';
import type { RevelioPageId } from '../../components/PageConfigProvider/types';
import { NO_PRIMARY_FILTER_KEY } from '../engine/filterGroupConsistency';
import {
  type Filter,
  FilterId,
  FilterType,
  type SingleValueFilter,
} from '../types/filters';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Reads the skill taxonomy id from the active filter map for AI Search API requests.
 * Used by both the AI Search filter form and the AI Assistant when requesting filter search.
 */
export function getSkillTaxonomyIdFromActiveFilterMap(
  activeFilterMap: Map<FilterId, Filter>
): number {
  const skillTaxonomyFilter = activeFilterMap.get(FilterId.SKILL_TAXONOMY);
  if (!skillTaxonomyFilter || skillTaxonomyFilter.type !== FilterType.VALUE) {
    return 0;
  }
  const value = (skillTaxonomyFilter as SingleValueFilter<unknown>).value;
  if (!isRecord(value)) return 0;
  const taxonomyId = value['taxonomyId'];
  return typeof taxonomyId === 'number' && Number.isFinite(taxonomyId)
    ? taxonomyId
    : 0;
}

/**
 * Reads the role taxonomy id from the active filter map for AI Search API requests.
 * Used by both the AI Search filter form and the AI Assistant when requesting filter search.
 */
export function getRoleTaxonomyIdForAiSearch(
  activeFilterMap: Map<FilterId, Filter>
): string {
  const roleTaxonomyFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY);
  if (!roleTaxonomyFilter || roleTaxonomyFilter.type !== FilterType.VALUE) {
    return 'custom_role_default';
  }
  const value = (roleTaxonomyFilter as SingleValueFilter<unknown>).value;
  if (!isRecord(value)) return 'custom_role_default';
  return roleTaxonomySelectionToAiSearchId(
    value as CustomRoleTaxonomySelection
  );
}

/**
 * Whether the role/skill taxonomy preference is "adaptive" for an AI Search
 * request. When adaptive, the taxonomy read from the filter map (above) is sent
 * as a fallback and the backend resolves the taxonomy from the prompt's
 * company/industry context instead.
 *
 * Read from the preference scope of the current page (`getPreferenceScope`) —
 * the same scope the filter map this request's `roles_tax`/`skills_tax` come
 * from is synced under. Adaptive is primarily a company-page concept (where
 * `useAdaptiveRoleTaxonomy` resolves the taxonomy from the selected companies);
 * on Talent Discovery (INDIVIDUAL scope) the preference is not adaptive.
 */
export function getTaxonomyAdaptiveForAiSearch(pageContext: {
  revelioPageId?: RevelioPageId;
  /** The active filter group key (`filterState.activeKey`); selects the scope. */
  primaryFilterId?: string;
}): {
  rolesTaxAdaptive: boolean;
  skillsTaxAdaptive: boolean;
} {
  const scope = getPreferenceScope({
    pageId: pageContext.revelioPageId,
    primaryFilterId: pageContext.primaryFilterId ?? NO_PRIMARY_FILTER_KEY,
  });
  const taxonomies =
    PreferenceBrowserStorage.get(scope)[UserPreferenceKey.Taxonomies];
  return {
    rolesTaxAdaptive: taxonomies?.role === 'adaptive',
    skillsTaxAdaptive: taxonomies?.skill === 'adaptive',
  };
}
